import { useState, useRef, useEffect } from "react";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { FaFlag } from "react-icons/fa";
import logo from "../assets/logo.PNG";
import p1 from "../assets/p1.jpeg";
import p2 from "../assets/p2.jpeg";
import p3 from "../assets/p3.jpeg";
import p4 from "../assets/p4.jpeg";
import p5 from "../assets/p5.jpeg";
import p6 from "../assets/p6.jpeg";
import p7 from "../assets/p7.jpeg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { width: 100%; height: 100%; overflow: hidden; user-select: none; }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeIn = keyframes`from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)}`;
const flareburst = keyframes`
  0%   { transform:scale(1);    box-shadow:0 0 0 0   rgba(251,146,60,0.8); }
  40%  { transform:scale(1.18); box-shadow:0 0 0 18px rgba(251,146,60,0);  }
  100% { transform:scale(1);    box-shadow:0 0 0 0   rgba(251,146,60,0);  }
`;
const likePulse = keyframes`
  0%   { transform:scale(1);    box-shadow:0 0 0 0   rgba(168,85,253,0.8); }
  40%  { transform:scale(1.18); box-shadow:0 0 0 18px rgba(168,85,253,0);  }
  100% { transform:scale(1);    box-shadow:0 0 0 0   rgba(168,85,253,0);  }
`;
const skipShake = keyframes`0%,100%{transform:translateX(0)}25%{transform:translateX(-4px)}75%{transform:translateX(4px)}`;
const stampIn = keyframes`
  0%   { opacity:0; transform:translate(-50%,-50%) scale(2.5) rotate(-15deg); }
  60%  { opacity:1; transform:translate(-50%,-50%) scale(0.92) rotate(-12deg); }
  100% { opacity:1; transform:translate(-50%,-50%) scale(1)    rotate(-12deg); }
`;
const cardExitL = keyframes`from{transform:translateX(0) rotate(0deg);opacity:1}to{transform:translateX(-520px) translateY(-60px) rotate(-18deg);opacity:0}`;
const cardExitR = keyframes`from{transform:translateX(0) rotate(0deg);opacity:1}to{transform:translateX(520px)  translateY(-60px) rotate(18deg); opacity:0}`;
const cardExitU = keyframes`from{transform:translateX(0) rotate(0deg);opacity:1}to{transform:translateX(0)      translateY(-600px) rotate(0deg); opacity:0}`;
const nextCardIn = keyframes`from{transform:scale(0.92) translateY(24px);opacity:0.6}to{transform:scale(1) translateY(0);opacity:1}`;
const flareParticle = keyframes`0%{transform:translate(0,0) scale(1);opacity:1}100%{transform:var(--tx) scale(0);opacity:0}`;
const countIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;

// ─── NEW: Flare Sequence Keyframes ────────────────────────────────────────────

const beamShoot = keyframes`
  0%   { transform: translateX(-50%) scaleY(0) translateY(0); opacity: 0; }
  10%  { opacity: 1; }
  40%  { transform: translateX(-50%) scaleY(1) translateY(0); opacity: 1; }
  80%  { transform: translateX(-50%) scaleY(1) translateY(-120vh); opacity: 0.6; }
  100% { transform: translateX(-50%) scaleY(1) translateY(-140vh); opacity: 0; }
`;

const haloExpand = keyframes`
  0%   { transform: translateX(-50%) translateY(-50%) scale(0); opacity: 0.9; }
  100% { transform: translateX(-50%) translateY(-50%) scale(6);  opacity: 0; }
`;

const cardGlow = keyframes`
  0%   { box-shadow: 0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08) inset; }
  30%  { box-shadow: 0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08) inset,
           0 0 40px 12px rgba(251,180,60,0.7), 0 0 80px 30px rgba(251,146,60,0.45); }
  60%  { box-shadow: 0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08) inset,
           0 0 60px 20px rgba(255,220,80,0.85), 0 0 120px 50px rgba(251,146,60,0.55); }
  80%  { box-shadow: 0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08) inset,
           0 0 30px 8px rgba(251,180,60,0.5), 0 0 60px 20px rgba(251,146,60,0.3); }
  100% { box-shadow: 0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.08) inset; }
`;

const glowOverlayPulse = keyframes`
  0%   { opacity: 0; }
  40%  { opacity: 0.5; }
  70%  { opacity: 0.65; }
  100% { opacity: 0; }
`;

const notifSlideIn = keyframes`
  0%   { opacity: 0; transform: translateY(calc(-50% + 40px)) scale(0.88); }
  60%  { opacity: 1; transform: translateY(calc(-50% - 6px))  scale(1.02); }
  100% { opacity: 1; transform: translateY(-50%)               scale(1);   }
`;

const notifFadeOut = keyframes`
  0%   { opacity: 1; transform: translateY(-50%)               scale(1);   }
  100% { opacity: 0; transform: translateY(calc(-50% - 24px))  scale(0.92); }
`;

const streakFade = keyframes`
  0%   { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0) translateY(-40px); }
`;

// ─── Profile Modal Keyframes ──────────────────────────────────────────────────

const modalSlideUp = keyframes`
  from { transform: translateX(-50%) translateY(100%); opacity: 0; }
  to   { transform: translateX(-50%) translateY(0);    opacity: 1; }
`;
const modalSlideDown = keyframes`
  from { transform: translateX(-50%) translateY(0);    opacity: 1; }
  to   { transform: translateX(-50%) translateY(100%); opacity: 0; }
`;
const backdropFadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const backdropFadeOut = keyframes`from{opacity:1}to{opacity:0}`;

// ─── Flare Beam Elements ──────────────────────────────────────────────────────

const FlareBeam = styled.div`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: 6px;
  height: 180px;
  background: linear-gradient(
    to top,
    rgba(251, 146, 60, 0),
    rgba(251, 200, 60, 0.9) 30%,
    rgba(255, 255, 255, 1) 60%,
    rgba(251, 200, 60, 0.7) 80%,
    rgba(251, 146, 60, 0)
  );
  border-radius: 999px;
  transform-origin: bottom center;
  transform: translateX(-50%) scaleY(0);
  pointer-events: none;
  z-index: 100;
  filter: blur(1.5px) drop-shadow(0 0 10px rgba(251, 180, 60, 0.9));
  animation: ${beamShoot} 0.75s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
`;

const BeamGlow = styled.div`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 200, 60, 0.8) 0%,
    rgba(251, 146, 60, 0) 70%
  );
  pointer-events: none;
  z-index: 99;
  animation: ${haloExpand} 0.6s ease-out forwards;
`;

const BeamStreak = styled.div`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: 3px;
  height: ${({ $h }) => $h}px;
  background: linear-gradient(to top, rgba(251, 146, 60, 0.8), transparent);
  border-radius: 999px;
  transform-origin: bottom center;
  pointer-events: none;
  z-index: 98;
  animation: ${streakFade} ${({ $dur }) => $dur}s ease-out forwards;
  animation-delay: ${({ $delay }) => $delay}s;
`;

// ─── Notification Overlay ─────────────────────────────────────────────────────

const NotifOverlay = styled.div`
  position: fixed;
  top: 50%;
  left: 16px;
  right: 16px;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 28px 24px 24px;
  border-radius: 28px;
  background: linear-gradient(
    145deg,
    #7c2d00 0%,
    #b45309 30%,
    #92400e 60%,
    #78350f 100%
  );
  border: 1px solid rgba(255, 200, 80, 0.4);
  box-shadow:
    0 0 0 1px rgba(255, 180, 60, 0.2),
    0 0 40px rgba(251, 146, 60, 0.5),
    0 0 80px rgba(251, 146, 60, 0.25),
    0 16px 48px rgba(0, 0, 0, 0.7);
  pointer-events: none;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 90% 60% at 50% -10%,
      rgba(255, 220, 80, 0.22) 0%,
      transparent 70%
    );
    pointer-events: none;
  }
  animation: ${({ $leaving }) => ($leaving ? notifFadeOut : notifSlideIn)} 0.45s
    cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const NotifIconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 38% 32%,
    #ffe57a 0%,
    #f9a825 50%,
    #e67e22 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  flex-shrink: 0;
  box-shadow:
    0 0 0 6px rgba(249, 168, 37, 0.2),
    0 0 24px rgba(249, 168, 37, 0.7);
`;
const NotifText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;
const NotifTitle = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 22px;
  font-weight: 600;
  font-style: italic;
  color: #fff;
  letter-spacing: 0.03em;
  text-shadow: 0 0 20px rgba(255, 200, 60, 0.6);
`;
const NotifSub = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(255, 220, 160, 0.85);
  line-height: 1.5;
`;
const NotifDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 180, 60, 0.4),
    transparent
  );
`;
const NotifCooldown = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 200, 120, 0.65);
  display: flex;
  align-items: center;
  gap: 6px;
  letter-spacing: 0.02em;
`;
const NotifFlareLabel = styled.span`
  color: #fbbf24;
  font-weight: 600;
`;

// ─── Profile Modal Styled Components ─────────────────────────────────────────

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: ${({ $closing }) => ($closing ? backdropFadeOut : backdropFadeIn)}
    0.35s ease forwards;
`;

const ModalSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 430px;
  height: 92vh;
  border-radius: 28px 28px 0 0;
  background: #0d0018;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  z-index: 301;
  animation: ${({ $closing }) => ($closing ? modalSlideDown : modalSlideUp)}
    0.42s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  box-shadow:
    0 -8px 40px rgba(116, 4, 190, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.08);
  &::-webkit-scrollbar {
    display: none;
  }
`;

/* Swipeable photo strip at the top */
const PhotoStrip = styled.div`
  position: relative;
  width: 90%;
  margin: 20px auto 0;
  border-radius: 20px;
  flex-shrink: 0;
  overflow: hidden;
  background: #1a0030;
`;

const PhotoTrack = styled.div`
  display: flex;
  width: ${({ $count }) => $count * 100}%;
  transform: translateX(${({ $index, $count }) => (-$index / $count) * 100}%);
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
`;

const PhotoSlide = styled.div`
  flex: 0 0 ${({ $count }) => 100 / $count}%;
`;

const PhotoImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  pointer-events: none;
`;

const PhotoGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.08) 0%,
    transparent 30%,
    transparent 55%,
    rgba(0, 0, 0, 0.75) 100%
  );
`;

/* Dot indicators */
const DotRow = styled.div`
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 5px;
  z-index: 4;
`;
const Dot = styled.div`
  width: ${({ $active }) => ($active ? 20 : 6)}px;
  height: 6px;
  border-radius: 100px;
  background: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.4)")};
  transition: all 0.25s ease;
`;

/* Close button */
const ModalCloseBtn = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.15);
`;

/* Flag pill top-left — reuse same style as card */
const ModalFlagWrap = styled.div`
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 10;
  display: inline-flex;
`;
const ModalFlagPill = styled.div`
  height: 28px;
  border-radius: 100px;
  background: rgba(220, 38, 38, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 6px;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
`;
const ModalFlagNotif = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ff3b30;
  border: 1.5px solid #0d0018;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Mendl", sans-serif;
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  z-index: 5;
`;
const ModalBodyPill = styled.div`
  position: absolute;
  top: 18px;
  right: 60px;
  z-index: 10;
  height: 28px;
  border-radius: 100px;
  background: rgba(16, 185, 129, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 5px;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
`;

/* Scrollable content below photo */
const ModalScroll = styled.div`
  padding: 20px 22px 40px;
`;

const ModalNameRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 4px;
`;
const ModalName = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 30px;
  font-weight: 400;
  color: #fff;
  letter-spacing: 0.02em;
`;
const ModalAge = styled.span`
  font-family: "Mendl", sans-serif;
  font-size: 24px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.65);
`;
const ModalHandle = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 16px;
  letter-spacing: 0.02em;
`;

const ModalDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  margin: 18px 0;
`;

const ModalSectionLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(168, 85, 253, 0.8);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 10px;
`;

const ModalBio = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.65;
  letter-spacing: 0.01em;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const Chip = styled.div`
  height: 30px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(8px);
  padding: 0 13px;
  display: flex;
  align-items: center;
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
`;

const ModalTagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;
const ModalTag = styled.div`
  height: 26px;
  border-radius: 100px;
  border: 1px solid rgba(168, 85, 253, 0.4);
  background: rgba(168, 85, 253, 0.12);
  padding: 0 11px;
  display: flex;
  align-items: center;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: rgba(200, 160, 255, 0.9);
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Screen = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  max-width: 430px;
  margin: 0 auto;
  background: linear-gradient(#7404be, #a855fd);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(
        ellipse 80% 50% at 50% -10%,
        #7404be44 0%,
        transparent 65%
      ),
      radial-gradient(
        ellipse 55% 35% at 15% 100%,
        #a855fd22 0%,
        transparent 60%
      );
    pointer-events: none;
    z-index: 0;
  }
`;

const TopBar = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 54px 24px 12px;
  display: flex;
  align-items: center;
  justify-content: space-around;
`;
const TopTitle = styled.h1`
  font-family: "Voltage", sans-serif;
  font-size: 28px;
  font-weight: 300;
  font-style: italic;
  color: #fff;
  letter-spacing: 0.04em;
`;
const CounterPill = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.06em;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 100px;
  padding: 5px 12px;
  animation: ${countIn} 0.3s ease both;
`;
const FilterBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
`;

const StackArea = styled.div`
  position: relative;
  z-index: 5;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px 0;
`;

const CardWrapper = styled.div`
  position: absolute;
  width: 100%;
  max-width: 390px;
  height: 100%;
  ${({ $isNext }) =>
    $isNext &&
    css`
      transform: scale(0.93) translateY(20px);
      z-index: 1;
      pointer-events: none;
    `}
  ${({ $isActive }) =>
    $isActive &&
    css`
      z-index: 2;
      animation: ${fadeIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
    `}
  ${({ $exitLeft }) =>
    $exitLeft &&
    css`
      animation: ${cardExitL} 0.45s cubic-bezier(0.4, 0, 0.6, 1) forwards;
      z-index: 3;
    `}
  ${({ $exitRight }) =>
    $exitRight &&
    css`
      animation: ${cardExitR} 0.45s cubic-bezier(0.4, 0, 0.6, 1) forwards;
      z-index: 3;
    `}
  ${({ $exitUp }) =>
    $exitUp &&
    css`
      animation: ${cardExitU} 0.45s cubic-bezier(0.4, 0, 0.6, 1) forwards;
      z-index: 3;
    `}
  ${({ $entering }) =>
    $entering &&
    css`
      animation: ${nextCardIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
      z-index: 2;
    `}
`;

const Card = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 28px;
  overflow: hidden;
  position: relative;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.7),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  cursor: grab;
  touch-action: none;
  will-change: transform;
  ${({ $dragX }) =>
    $dragX !== 0 &&
    css`
      transform: translateX(${$dragX}px) rotate(${$dragX * 0.04}deg);
      transition: none;
    `}
  ${({ $glowing }) =>
    $glowing &&
    css`
      animation: ${cardGlow} 0.9s ease-out both;
    `}
  &:active {
    cursor: grabbing;
  }
`;

const GlowOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 28px;
  z-index: 6;
  pointer-events: none;
  background: radial-gradient(
    ellipse 80% 70% at 50% 40%,
    rgba(255, 210, 80, 0.75) 0%,
    rgba(251, 146, 60, 0.45) 40%,
    transparent 75%
  );
  animation: ${glowOverlayPulse} 0.9s ease-out both;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
`;
const CardGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.08) 0%,
    transparent 30%,
    transparent 45%,
    rgba(0, 0, 0, 0.55) 75%,
    rgba(0, 0, 0, 0.88) 100%
  );
`;

const CardTopRow = styled.div`
  position: absolute;
  top: 18px;
  left: 18px;
  right: 18px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  z-index: 4;
`;

const FlagPillWrap = styled.div`
  position: relative;
  display: inline-flex;
`;
const FlagPill = styled.div`
  height: 28px;
  border-radius: 100px;
  background: rgba(220, 38, 38, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 6px;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
`;
const FlagNotif = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ff3b30;
  border: 1.5px solid #0a0010;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Mendl", sans-serif;
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  z-index: 5;
`;
const BodyCountPill = styled.div`
  height: 28px;
  border-radius: 100px;
  background: rgba(16, 185, 129, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  padding: 0 10px;
  gap: 5px;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
`;

const SwipeTint = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 28px;
  z-index: 5;
  pointer-events: none;
  opacity: ${({ $opacity }) => $opacity};
  background: ${({ $dir }) =>
    $dir === "right"
      ? "linear-gradient(135deg, rgba(168,85,253,0.5) 0%, transparent 70%)"
      : $dir === "left"
        ? "linear-gradient(225deg, rgba(239,68,68,0.5) 0%, transparent 70%)"
        : "transparent"};
  transition: opacity 0.05s linear;
`;

const DragStamp = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-12deg);
  z-index: 10;
  padding: 8px 18px;
  border-radius: 8px;
  border: 3px solid ${({ $color }) => $color};
  font-family: "Mendl", sans-serif;
  font-size: 34px;
  font-weight: 600;
  font-style: italic;
  color: ${({ $color }) => $color};
  letter-spacing: 0.1em;
  text-shadow: 0 0 20px ${({ $color }) => $color}88;
  opacity: ${({ $opacity }) => $opacity};
  pointer-events: none;
  white-space: nowrap;
`;

const CardBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 22px 26px;
  z-index: 4;
`;
const NameRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 3px;
`;
const ProfileName = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 32px;
  font-weight: 400;
  color: #fff;
  letter-spacing: 0.02em;
`;
const ProfileAge = styled.span`
  font-family: "Mendl", sans-serif;
  font-size: 26px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.75);
`;
const ProfileHandle = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 12px;
  letter-spacing: 0.02em;
`;
const TagsRow = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;
const Tag = styled.div`
  height: 26px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  padding: 0 11px;
  display: flex;
  align-items: center;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
`;
const ViewProfileBtn = styled.button`
  height: 34px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  letter-spacing: 0.02em;
`;

const Stamp = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-12deg);
  z-index: 10;
  padding: 10px 22px;
  border-radius: 8px;
  border: 3px solid ${({ $color }) => $color};
  font-family: "Mendl", sans-serif;
  font-size: 38px;
  font-weight: 600;
  font-style: italic;
  color: ${({ $color }) => $color};
  letter-spacing: 0.1em;
  text-shadow: 0 0 20px ${({ $color }) => $color}88;
  box-shadow: 0 0 30px ${({ $color }) => $color}44;
  white-space: nowrap;
  animation: ${stampIn} 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
  pointer-events: none;
`;

// ─── Particles ────────────────────────────────────────────────────────────────

const Particle = styled.div`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  top: 50%;
  left: 50%;
  pointer-events: none;
  z-index: 20;
  --tx: ${({ $tx }) => $tx};
  animation: ${flareParticle} ${({ $dur }) => $dur}s ease-out forwards;
`;
function FlareParticles() {
  const particles = Array.from({ length: 12 }, (_, i) => {
    const angle = (i / 12) * 360,
      dist = 60 + Math.random() * 50;
    const rad = (angle * Math.PI) / 180;
    return {
      tx: `translate(${Math.cos(rad) * dist}px,${Math.sin(rad) * dist}px)`,
      size: 4 + Math.random() * 6,
      color: ["#fb923c", "#fbbf24", "#f87171", "#fde68a"][
        Math.floor(Math.random() * 4)
      ],
      dur: 0.4 + Math.random() * 0.4,
    };
  });
  return (
    <>
      {particles.map((p, i) => (
        <Particle
          key={i}
          $tx={p.tx}
          $size={p.size}
          $color={p.color}
          $dur={p.dur}
        />
      ))}
    </>
  );
}

// ─── Action Buttons ───────────────────────────────────────────────────────────

const ActionsRow = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 16px 24px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const btnBase = `
  position: relative; border: none; cursor: pointer; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.18s cubic-bezier(0.23,1,0.32,1), box-shadow 0.18s ease;
  &:active { transform: scale(0.88); }
  &:hover  { transform: scale(1.06); }
`;

const ActionBtn = styled.button`
  ${({ $animate, $type }) =>
    $animate &&
    $type === "flare" &&
    css`
      animation: ${flareburst} 0.5s ease forwards;
    `}
  ${({ $animate, $type }) =>
    $animate &&
    $type === "like" &&
    css`
      animation: ${likePulse} 0.5s ease forwards;
    `}
`;

const DislikeBtn = styled(ActionBtn)`
  ${btnBase} width:64px;
  height: 64px;
  background: radial-gradient(
    circle at 38% 32%,
    #585858 0%,
    #2e2e2e 55%,
    #474444 100%
  );
  box-shadow:
    0 0 0 2px rgba(255, 255, 255, 0.07),
    0 2px 0 1px rgba(255, 255, 255, 0.13) inset,
    0 -2px 0 1px rgba(0, 0, 0, 0.55) inset,
    0 8px 20px rgba(0, 0, 0, 0.6);
  color: rgba(210, 210, 210, 0.95);
  font-size: 22px;
  &::before {
    content: "";
    position: absolute;
    top: 14%;
    left: 22%;
    right: 22%;
    height: 26%;
    border-radius: 50%;
    background: radial-gradient(
      ellipse,
      rgba(255, 255, 255, 0.18) 0%,
      transparent 70%
    );
    pointer-events: none;
  }
`;

const LikeBtn = styled(ActionBtn)`
  ${btnBase} width:64px;
  height: 64px;
  background: radial-gradient(
    circle at 38% 32%,
    #f48ab0 0%,
    #e0457b 50%,
    #c0305e 100%
  );
  box-shadow:
    0 0 0 2px rgba(255, 255, 255, 0.12),
    0 2px 0 1px rgba(255, 255, 255, 0.38) inset,
    0 -2px 0 1px rgba(0, 0, 0, 0.28) inset,
    0 8px 28px rgba(224, 69, 123, 0.6),
    0 0 40px rgba(224, 69, 123, 0.22);
  color: #fff;
  font-size: 22px;
  &::before {
    content: "";
    position: absolute;
    top: 14%;
    left: 22%;
    right: 22%;
    height: 28%;
    border-radius: 50%;
    background: radial-gradient(
      ellipse,
      rgba(255, 255, 255, 0.5) 0%,
      transparent 70%
    );
    pointer-events: none;
  }
`;

const twinkle = keyframes`
  0%,100%{opacity:0;transform:scale(0.4) rotate(0deg);}
  50%    {opacity:1;transform:scale(1.1) rotate(20deg);}
`;
const FlareSparkle = styled.div`
  position: absolute;
  width: ${({ $s }) => $s}px;
  height: ${({ $s }) => $s}px;
  top: ${({ $top }) => $top}%;
  left: ${({ $left }) => $left}%;
  pointer-events: none;
  animation: ${twinkle} ${({ $dur }) => $dur}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: #fff9c4;
    clip-path: polygon(
      50% 0%,
      55% 45%,
      100% 50%,
      55% 55%,
      50% 100%,
      45% 55%,
      0% 50%,
      45% 45%
    );
  }
`;

const FlareBtn = styled(ActionBtn)`
  ${btnBase} width:78px;
  height: 78px;
  background: radial-gradient(
    circle at 38% 30%,
    #ffe57a 0%,
    #f9a825 42%,
    #acacac 100%
  );
  box-shadow:
    0 0 0 2.5px rgba(156, 150, 150, 0.18),
    0 2px 0 1px rgba(255, 255, 255, 0.5) inset,
    0 -2px 0 1px rgba(0, 0, 0, 0.35) inset,
    0 10px 32px rgba(119, 119, 118, 0.75),
    0 0 55px rgba(107, 105, 103, 0.35);
  color: #ffffff;
  font-size: 28px;
  flex-direction: column;
  gap: 1px;
  &::before {
    content: "";
    position: absolute;
    top: 13%;
    left: 18%;
    right: 18%;
    height: 30%;
    border-radius: 50%;
    background: radial-gradient(
      ellipse,
      rgba(255, 255, 255, 0.6) 0%,
      transparent 70%
    );
    pointer-events: none;
    z-index: 1;
  }
  span.label {
    font-family: "Mendl", sans-serif;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.9);
    text-transform: uppercase;
    line-height: 1;
    position: relative;
    z-index: 2;
  }
`;

// ─── Bottom Nav ───────────────────────────────────────────────────────────────

const NavBar = styled.nav`
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 10px 20px 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const NavPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  height: 64px;
  border-radius: 100px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(#3d2253, #1f0e2a);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.22),
    0 1.5px 0 1px rgba(255, 255, 255, 0.28) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.2) inset,
    0 8px 32px rgba(0, 0, 0, 0.45),
    0 2px 8px rgba(0, 0, 0, 0.3);
  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 14%;
    right: 14%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );
    border-radius: 50%;
    pointer-events: none;
  }
`;
const NavItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  border: none;
  cursor: pointer;
  height: 100%;
  padding: 0 4px;
  font-family: "Mendl", sans-serif;
  font-size: 9.5px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.4)")};
  letter-spacing: 0.04em;
  transition: color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  svg {
    font-size: 19px;
  }
  &:active {
    opacity: 0.7;
  }
`;
const NavActiveBg = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ $index, $count }) =>
    `calc(${(($index + 0.5) / $count) * 100}% - 28px)`};
  transform: translateY(-50%);
  width: 56px;
  height: 46px;
  border-radius: 20px;
  background: rgba(168, 85, 253, 0.22);
  border: 1px solid rgba(168, 85, 253, 0.35);
  pointer-events: none;
  transition: left 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;
const NavDot = styled.div`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #a855fd;
  margin-top: -1px;
`;

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconX = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconHeart = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const IconFlare = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z" />
  </svg>
);
const IconEye = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconMatches = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const IconMsg = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconUser = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconHelp = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ─── Data ─────────────────────────────────────────────────────────────────────

const PROFILES = [
  {
    id: 1,
    name: "Amanda",
    age: 21,
    handle: "amanda.vita",
    notif: 2,
    bodyCount: 3,
    tags: ["Hook-ups", "Relationships"],
    img: p4,
    bio: "Living for sunsets, espresso, and spontaneous road trips. I believe the best conversations happen at 2am. Half Italian, whole trouble.",
    hobbies: ["Hiking", "Vintage Shopping", "Film Photography", "Pasta Making"],
    interests: ["Travel", "Astrology", "True Crime", "Indie Music"],
    photos: [
      p4,
      "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=600&q=80",
      "https://images.unsplash.com/photo-1514315384763-ba401779410f?w=600&q=80",
      "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600&q=80",
    ],
  },
  {
    id: 2,
    name: "Lucas",
    age: 29,
    handle: "lucas.br",
    notif: null,
    bodyCount: 7,
    tags: ["Casual", "Friendships"],
    img: p1,
    bio: "Software engineer by day, amateur chef by night. Fluent in sarcasm and four programming languages. Looking for someone to debate pizza toppings with.",
    hobbies: ["Cooking", "Gym", "Chess", "Skateboarding"],
    interests: ["Tech", "Football", "Stand-up Comedy", "Coffee"],
    photos: [
      p1,
      "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=600&q=80",
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&q=80",
      "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=600&q=80",
    ],
  },
  {
    id: 3,
    name: "Mia",
    age: 24,
    handle: "mia.paris",
    notif: 1,
    bodyCount: 2,
    tags: ["Relationships", "Romance"],
    img: p2,
    bio: "Parisian at heart, wherever I am. I spend my weekends at art galleries and my evenings reading novels I'll never finish. Looking for something real.",
    hobbies: ["Painting", "Reading", "Ballet", "Café hopping"],
    interests: ["Art", "Literature", "Fashion", "Cinema"],
    photos: [
      p2,
      "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=600&q=80",
      "https://images.unsplash.com/photo-1488716820095-cbe80883c496?w=600&q=80",
      "https://images.unsplash.com/photo-1519699047748-de8e457a634e?w=600&q=80",
    ],
  },
  {
    id: 4,
    name: "Richard",
    age: 31,
    handle: "rich.nyc",
    notif: 2,
    bodyCount: 11,
    tags: ["Hook-ups", "Casual"],
    img: p3,
    bio: "NYC-based architect with an unhealthy obsession with exposed brick and good bourbon. I'll charm you with dad jokes and cook you brunch.",
    hobbies: ["Architecture", "Bourbon Tasting", "Running", "Jazz"],
    interests: ["Design", "History", "Sports", "Food"],
    photos: [
      p3,
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80",
      "https://images.unsplash.com/photo-1463453091185-61582044d556?w=600&q=80",
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&q=80",
    ],
  },
  {
    id: 5,
    name: "Aisha",
    age: 27,
    handle: "aisha.ng",
    notif: null,
    bodyCount: 4,
    tags: ["Deep Talks", "Romance"],
    img: p5,
    bio: "Lagos → London. I'm a documentary filmmaker who cries at beautiful cinematography. Let's talk about things that actually matter over jollof rice.",
    hobbies: ["Filmmaking", "Afrobeats Dancing", "Journaling", "Yoga"],
    interests: ["Social Justice", "African Cinema", "Poetry", "Wellness"],
    photos: [
      p5,
      "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=600&q=80",
      "https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=600&q=80",
      "https://images.unsplash.com/photo-1502823403499-6ccfcf4fb453?w=600&q=80",
    ],
  },
  {
    id: 6,
    name: "Elena",
    age: 25,
    handle: "elena.gr",
    notif: 1,
    bodyCount: 1,
    tags: ["Relationships"],
    img: p7,
    bio: "Thessaloniki girl navigating London. Marine biologist who spends more time in the ocean than in bars. I will absolutely talk about dolphins unprompted.",
    hobbies: ["Diving", "Cooking Greek food", "Hiking", "Photography"],
    interests: ["Marine Life", "Environmentalism", "Travel", "Mythology"],
    photos: [
      p7,
      "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&q=80",
      "https://images.unsplash.com/photo-1520813792240-56fc4a3765a7?w=600&q=80",
      "https://images.unsplash.com/photo-1496440737103-cd596325d314?w=600&q=80",
    ],
  },
  {
    id: 7,
    name: "Noah",
    age: 28,
    handle: "noah.uk",
    notif: null,
    bodyCount: 9,
    tags: ["Hook-ups", "Friendships"],
    img: p6,
    bio: "Manchester lad, terrible at texting back but great in person. Music producer who takes his vinyl collection way too seriously. Let's cause some trouble.",
    hobbies: ["Music Production", "Record Digging", "Football", "Cycling"],
    interests: ["Electronic Music", "Street Art", "Gaming", "Sneakers"],
    photos: [
      p6,
      "https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=600&q=80",
      "https://images.unsplash.com/photo-1534614971-6be99a7a3ffd?w=600&q=80",
      "https://images.unsplash.com/photo-1549078642-b2ba4bda0cdb?w=600&q=80",
    ],
  },
];

const SWIPE_THRESHOLD = 80;
const FLARE_IDLE = "idle";
const FLARE_BEAM = "beam";
const FLARE_GLOW = "glow";
const FLARE_NOTIF = "notif";
const FLARE_LEAVING = "leaving";

// ─── Profile Modal Component ──────────────────────────────────────────────────

function ProfileModal({ profile, onClose }) {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [closing, setClosing] = useState(false);
  const touchStartX = useRef(null);

  function handleClose() {
    setClosing(true);
    setTimeout(onClose, 380);
  }

  function onPhotoTouchStart(e) {
    touchStartX.current = e.touches[0].clientX;
  }
  function onPhotoTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx < -40 && photoIndex < profile.photos.length - 1)
      setPhotoIndex((i) => i + 1);
    if (dx > 40 && photoIndex > 0) setPhotoIndex((i) => i - 1);
  }

  return (
    <>
      <ModalBackdrop $closing={closing} onClick={handleClose} />
      <ModalSheet $closing={closing}>
        {/* ── Photo strip ── */}
        <PhotoStrip
          onTouchStart={onPhotoTouchStart}
          onTouchEnd={onPhotoTouchEnd}
        >
          <PhotoTrack $count={profile.photos.length} $index={photoIndex}>
            {profile.photos.map((src, i) => (
              <PhotoSlide key={i} $count={profile.photos.length}>
                <PhotoImg
                  src={src}
                  alt={`${profile.name} photo ${i + 1}`}
                  draggable="false"
                />
                <PhotoGradient />
              </PhotoSlide>
            ))}
          </PhotoTrack>

          {/* Dot indicators */}
          <DotRow>
            {profile.photos.map((_, i) => (
              <Dot
                key={i}
                $active={i === photoIndex}
                onClick={() => setPhotoIndex(i)}
              />
            ))}
          </DotRow>

          {/* Flag pill — top left */}
          <ModalFlagWrap>
            <ModalFlagPill>
              <FaFlag style={{ fontSize: 11 }} />
            </ModalFlagPill>
            {profile.notif && <ModalFlagNotif>{profile.notif}</ModalFlagNotif>}
          </ModalFlagWrap>

          {/* Counts — top right (leave room for close btn) */}
          <ModalBodyPill>Counts: {profile.bodyCount}</ModalBodyPill>

          {/* Close button */}
          <ModalCloseBtn onClick={handleClose}>✕</ModalCloseBtn>
        </PhotoStrip>

        {/* ── Scrollable info ── */}
        <ModalScroll>
          <ModalNameRow>
            <ModalName>{profile.name}</ModalName>
            <ModalAge>{profile.age}</ModalAge>
          </ModalNameRow>
          <ModalHandle>@{profile.handle}</ModalHandle>

          <ModalTagsRow>
            {profile.tags.map((t) => (
              <ModalTag key={t}>{t}</ModalTag>
            ))}
          </ModalTagsRow>

          <ModalDivider />

          <ModalSectionLabel>About</ModalSectionLabel>
          <ModalBio>{profile.bio}</ModalBio>

          <ModalDivider />

          <ModalSectionLabel>Hobbies</ModalSectionLabel>
          <ChipRow>
            {profile.hobbies.map((h) => (
              <Chip key={h}>{h}</Chip>
            ))}
          </ChipRow>

          <ModalDivider />

          <ModalSectionLabel>Interests</ModalSectionLabel>
          <ChipRow>
            {profile.interests.map((i) => (
              <Chip key={i}>{i}</Chip>
            ))}
          </ChipRow>
        </ModalScroll>
      </ModalSheet>
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DiscoverPage() {
  const [queue, setQueue] = useState(PROFILES);
  const [stamp, setStamp] = useState(null);
  const [exitDir, setExitDir] = useState(null);
  const [animBtn, setAnimBtn] = useState(null);
  const [showParticles, setShowParticles] = useState(false);
  const [entering, setEntering] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragStamp, setDragStamp] = useState(null);

  // Profile modal state
  const [viewingProfile, setViewingProfile] = useState(null);

  // Flare sequence state
  const [flareStage, setFlareStage] = useState(FLARE_IDLE);
  const [flareOrigin, setFlareOrigin] = useState({ x: 0, y: 0 });
  const flareBtnRef = useRef(null);

  const exitingRef = useRef(false);
  const dragStart = useRef(null);
  const cardRef = useRef(null);

  const current = queue[0];
  const next = queue[1];

  // ── Flare sequence orchestration ─────────────────────────────────────────
  useEffect(() => {
    if (flareStage === FLARE_IDLE) return;
    if (flareStage === FLARE_BEAM) {
      const t = setTimeout(() => setFlareStage(FLARE_GLOW), 750);
      return () => clearTimeout(t);
    }
    if (flareStage === FLARE_GLOW) {
      const t = setTimeout(() => setFlareStage(FLARE_NOTIF), 900);
      return () => clearTimeout(t);
    }
    if (flareStage === FLARE_NOTIF) {
      const t = setTimeout(() => setFlareStage(FLARE_LEAVING), 2000);
      return () => clearTimeout(t);
    }
    if (flareStage === FLARE_LEAVING) {
      const t = setTimeout(() => {
        setFlareStage(FLARE_IDLE);
        setStamp({ label: "🔥 FLARE", color: "#fb923c" });
        setExitDir("right");
        setShowParticles(true);
        setTimeout(() => {
          setQueue((q) => q.slice(1));
          setStamp(null);
          setExitDir(null);
          setShowParticles(false);
          setEntering(true);
          exitingRef.current = false;
          setTimeout(() => setEntering(false), 450);
        }, 440);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [flareStage]);

  // ── Core action ───────────────────────────────────────────────────────────
  function triggerAction(type) {
    if (exitingRef.current || !current) return;

    if (type === "flare") {
      if (flareBtnRef.current) {
        const rect = flareBtnRef.current.getBoundingClientRect();
        setFlareOrigin({
          x: rect.left + rect.width / 2,
          y: window.innerHeight - rect.top,
        });
      }
      exitingRef.current = true;
      setAnimBtn("flare");
      setTimeout(() => setAnimBtn(null), 500);
      setFlareStage(FLARE_BEAM);
      return;
    }

    exitingRef.current = true;
    setDragX(0);
    setDragStamp(null);
    setAnimBtn(type);
    setTimeout(() => setAnimBtn(null), 500);

    const dir = type === "like" ? "right" : type === "dislike" ? "left" : "up";
    const stampLabel =
      type === "like" ? "LIKE" : type === "dislike" ? "NOPE" : "SKIP";
    const stampColor =
      type === "like"
        ? "#a855fd"
        : type === "dislike"
          ? "#ef4444"
          : "rgba(255,255,255,0.6)";

    setStamp({ label: stampLabel, color: stampColor });
    setExitDir(dir);
    setTimeout(() => {
      setQueue((q) => q.slice(1));
      setStamp(null);
      setExitDir(null);
      setShowParticles(false);
      setEntering(true);
      exitingRef.current = false;
      setTimeout(() => setEntering(false), 450);
    }, 440);
  }

  // ── Touch / Mouse swipe handlers ──────────────────────────────────────────
  function getClientX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }
  function onDragStart(e) {
    if (exitingRef.current) return;
    dragStart.current = { x: getClientX(e), time: Date.now() };
  }
  function onDragMove(e) {
    if (!dragStart.current || exitingRef.current) return;
    const dx = getClientX(e) - dragStart.current.x;
    setDragX(dx);
    const ratio = Math.min(Math.abs(dx) / SWIPE_THRESHOLD, 1);
    if (Math.abs(dx) > SWIPE_THRESHOLD * 0.35) {
      setDragStamp({
        label: dx > 0 ? "LIKE" : "NOPE",
        color: dx > 0 ? "#a855fd" : "#ef4444",
        opacity: ratio * 0.9,
      });
    } else {
      setDragStamp(null);
    }
  }
  function onDragEnd(e) {
    if (!dragStart.current || exitingRef.current) return;
    const dx = dragX;
    dragStart.current = null;
    if (Math.abs(dx) >= SWIPE_THRESHOLD) {
      setDragX(0);
      triggerAction(dx > 0 ? "like" : "dislike");
    } else {
      setDragX(0);
      setDragStamp(null);
    }
  }

  // ── Streak trails ─────────────────────────────────────────────────────────
  const streaks =
    flareStage === FLARE_BEAM
      ? Array.from({ length: 5 }, (_, i) => ({
          id: i,
          offsetX: (Math.random() - 0.5) * 30,
          h: 60 + Math.random() * 80,
          dur: 0.4 + Math.random() * 0.3,
          delay: Math.random() * 0.2,
        }))
      : [];

  // ── Empty state ───────────────────────────────────────────────────────────
  if (queue.length === 0) {
    return (
      <>
        <GlobalStyle />
        <Screen
          style={{ alignItems: "center", justifyContent: "center", gap: 16 }}
        >
          <div style={{ fontSize: 48 }}>✨</div>
          <p
            style={{
              fontFamily: "'Cormorant Garamond',serif",
              fontSize: 26,
              fontStyle: "italic",
              color: "#fff",
              opacity: 0.7,
            }}
          >
            You've seen everyone
          </p>
          <p
            style={{
              fontFamily: "'DM Sans',sans-serif",
              fontSize: 13,
              color: "rgba(255,255,255,0.35)",
            }}
          >
            Check back soon for new people
          </p>
        </Screen>
      </>
    );
  }

  const swipeTintDir = dragX > 20 ? "right" : dragX < -20 ? "left" : null;
  const swipeTintOpacity = Math.min(Math.abs(dragX) / SWIPE_THRESHOLD, 1) * 0.7;
  const isGlowing = flareStage === FLARE_GLOW;

  return (
    <>
      <GlobalStyle />
      <Screen>
        {/* Profile modal */}
        {viewingProfile && (
          <ProfileModal
            profile={viewingProfile}
            onClose={() => setViewingProfile(null)}
          />
        )}

        {/* Flare beam + glow */}
        {flareStage === FLARE_BEAM && (
          <>
            <BeamGlow $bottom={flareOrigin.y} $left={flareOrigin.x} />
            <FlareBeam $bottom={flareOrigin.y} $left={flareOrigin.x} />
            {streaks.map((s) => (
              <BeamStreak
                key={s.id}
                $bottom={flareOrigin.y}
                $left={flareOrigin.x + s.offsetX}
                $h={s.h}
                $dur={s.dur}
                $delay={s.delay}
              />
            ))}
          </>
        )}

        {/* Flare notification */}
        {(flareStage === FLARE_NOTIF || flareStage === FLARE_LEAVING) &&
          current && (
            <NotifOverlay $leaving={flareStage === FLARE_LEAVING}>
              <NotifIconWrap>🔥</NotifIconWrap>
              <NotifText>
                <NotifTitle>{current.name} just got Flared!</NotifTitle>
                <NotifSub>
                  Your <NotifFlareLabel>Flare</NotifFlareLabel> is flying their
                  way — they'll definitely notice you.
                </NotifSub>
              </NotifText>
              <NotifDivider />
              <NotifCooldown>
                ⏳ You've used your 1 daily Flare — next one available in 24
                hours
              </NotifCooldown>
            </NotifOverlay>
          )}

        {/* Top bar */}
        <TopBar>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <img
              src={logo}
              alt=""
              style={{ width: 40, opacity: 0.9, marginBottom: 2 }}
            />
            <TopTitle>Discover</TopTitle>
          </div>
          <CounterPill key={current.id}>
            {PROFILES.length - queue.length + 1} of {PROFILES.length}
          </CounterPill>
          <FilterBtn>⚙</FilterBtn>
        </TopBar>

        {/* Card stack */}
        <StackArea>
          {next && (
            <CardWrapper $isNext={!entering} $entering={entering}>
              <Card>
                <CardImage src={next.img} alt={next.name} draggable="false" />
                <CardGradient />
              </Card>
            </CardWrapper>
          )}

          <CardWrapper
            $isActive={!exitDir && !isGlowing}
            $exitLeft={exitDir === "left"}
            $exitRight={exitDir === "right"}
            $exitUp={exitDir === "up"}
            $glowing={isGlowing}
          >
            <Card
              ref={cardRef}
              $dragX={exitDir ? 0 : dragX}
              $glowing={isGlowing}
              onMouseDown={onDragStart}
              onMouseMove={onDragMove}
              onMouseUp={onDragEnd}
              onMouseLeave={onDragEnd}
              onTouchStart={onDragStart}
              onTouchMove={onDragMove}
              onTouchEnd={onDragEnd}
            >
              <CardImage
                src={current.img}
                alt={current.name}
                draggable="false"
              />
              <CardGradient />
              {isGlowing && <GlowOverlay />}

              {!exitDir && (
                <SwipeTint $dir={swipeTintDir} $opacity={swipeTintOpacity} />
              )}

              <CardTopRow>
                <FlagPillWrap>
                  <FlagPill>
                    <FaFlag style={{ fontSize: 11 }} />
                  </FlagPill>
                  {current.notif && <FlagNotif>{current.notif}</FlagNotif>}
                </FlagPillWrap>
                <BodyCountPill>Counts: {current.bodyCount}</BodyCountPill>
              </CardTopRow>

              {!exitDir && dragStamp && (
                <DragStamp
                  $color={dragStamp.color}
                  $opacity={dragStamp.opacity}
                >
                  {dragStamp.label}
                </DragStamp>
              )}
              {stamp && <Stamp $color={stamp.color}>{stamp.label}</Stamp>}
              {showParticles && <FlareParticles />}

              <CardBottom>
                <NameRow>
                  <ProfileName>{current.name}</ProfileName>
                  <ProfileAge>{current.age}</ProfileAge>
                </NameRow>
                <ProfileHandle>@{current.handle}</ProfileHandle>
                <TagsRow>
                  {current.tags.map((t) => (
                    <Tag key={t}>{t}</Tag>
                  ))}
                </TagsRow>
                <ViewProfileBtn onClick={() => setViewingProfile(current)}>
                  <IconEye /> View Profile
                </ViewProfileBtn>
              </CardBottom>
            </Card>
          </CardWrapper>
        </StackArea>

        {/* Action buttons */}
        <ActionsRow>
          <DislikeBtn
            $animate={animBtn === "dislike"}
            $type="dislike"
            onClick={() => triggerAction("dislike")}
            title="Dislike"
          >
            <IconX />
          </DislikeBtn>
          <FlareBtn
            ref={flareBtnRef}
            $animate={animBtn === "flare"}
            $type="flare"
            onClick={() => triggerAction("flare")}
            title="Flare"
          >
            <FlareSparkle $s={7} $top={8} $left={72} $dur={1.4} $delay={0} />
            <FlareSparkle $s={5} $top={72} $left={8} $dur={1.7} $delay={0.5} />
            <FlareSparkle $s={6} $top={14} $left={12} $dur={1.5} $delay={0.9} />
            <FlareSparkle $s={4} $top={68} $left={76} $dur={1.3} $delay={0.3} />
            <IconFlare />
            <span className="label">Flare</span>
          </FlareBtn>
          <LikeBtn
            $animate={animBtn === "like"}
            $type="like"
            onClick={() => triggerAction("like")}
            title="Like"
          >
            <IconHeart />
          </LikeBtn>
        </ActionsRow>

        {/* Bottom Nav */}
        <NavBar>
          <NavPill>
            <NavActiveBg $index={1} $count={5} />
            <NavItem>
              <IconMatches />
              <span>Matches</span>
            </NavItem>
            <NavItem $active>
              <IconFlare />
              <span>Discover</span>
              <NavDot />
            </NavItem>
            <NavItem>
              <IconMsg />
              <span>Messages</span>
            </NavItem>
            <NavItem>
              <IconUser />
              <span>Profile</span>
            </NavItem>
            <NavItem>
              <IconHelp />
              <span>Help</span>
            </NavItem>
          </NavPill>
        </NavBar>
      </Screen>
    </>
  );
}
